import './index.css'
import cn from 'clsx'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updateParams } from '@disco/state-management/actions/StatsActions'
import { getTrialStartDate } from '@disco/state-management/selectors/user'
import DateRangePicker from 'components/common/date-range-picker'
type Props = {
  disabled?: boolean
}

/**
 * StatsDateRange component for selecting date ranges in analytics
 *
 * A functional component that provides a date range picker for filtering analytics data.
 * Integrates with Redux state management and includes analytics event tracking.
 * Supports predefined date ranges and respects trial start date as minimum boundary.
 *
 * @param props - Component props
 * @param props.disabled - Whether the date range picker is disabled
 * @returns JSX element containing the date range picker with label
 */
const StatsDateRange = ({ disabled }: Props) => {
  const dispatch = useDispatch()
  const stats: State['stats'] = useSelector(state => state.stats)
  const trialStartDate: Date = useSelector(getTrialStartDate)
  const handleChange = useCallback(
    (startDate, endDate) => {
      dispatch(
        updateParams({
          startDate: startDate || endDate,
          endDate: endDate || startDate,
        }),
      )
      dispatch(sendMixpanelEvent('Show new period in analytics'))
    },
    [dispatch],
  )
  return (
    <>
      <label
        className={`Form_Label Stats_Label ${cn({
          'Stats_Label--Disabled': disabled,
        })}`}
      >
        Select period
      </label>
      <DateRangePicker
        autoApply
        applyButton={false}
        disabled={disabled}
        dropdownOpenClassName="StatsDateRange-dropdown-open"
        endDate={stats.endDate}
        maxDate={new Date()}
        showPredefinedRanges
        startDate={stats.startDate}
        minDate={trialStartDate}
        onChange={handleChange}
      />
    </>
  )
}

export default React.memo<Props>(StatsDateRange)
